import React, { useState, useEffect } from 'react';
import { Table, Input, Select, Radio, Checkbox, Collapse, Typography, message } from 'antd';
import { DownOutlined, RightOutlined, SearchOutlined } from '@ant-design/icons';

const { Search } = Input;
const { Text } = Typography;

/**
 * AdvancedTableDisplay - Configurable table with sorting, filtering, searching, selection, and clickable columns
 * 
 * @param {Object} props
 * @param {Object} props.data - Table configuration and data
 * @param {Array} props.data.columns - Column definitions with sortable, searchable, filterable, clickable configs
 * @param {Array} props.data.dataSource - Table data rows
 * @param {number} props.data.pageSize - Rows per page (default: 10)
 * @param {string} props.data.selectionMode - 'single', 'multiple', or null
 * @param {string} props.title - Table title
 * @param {string} props.uuid - Component UUID
 * @param {string} props.sessionId - Session ID
 * @param {Function} props.onComponentUpdate - Callback to refresh all components with new apiData
 */
const AdvancedTableDisplay = ({
  data,
  title = "Table",
  uuid,
  sessionId,
  onComponentUpdate
}) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const [searchText, setSearchText] = useState({});
  const [filteredData, setFilteredData] = useState([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [loading, setLoading] = useState(false);

  const columns = data?.columns || [];
  const dataSource = data?.dataSource || [];
  const pageSize = data?.pageSize || 10;
  const selectionMode = data?.selectionMode || null;

  useEffect(() => {
    setFilteredData(dataSource);
  }, [dataSource]);

  // Build Ant Design columns with sorting, filtering, searching
  const buildColumns = () => {
    return columns.map(col => {
      const column = {
        title: col.title,
        dataIndex: col.dataIndex,
        key: col.key,
        width: col.width
      };

      // Sortable
      if (col.sortable) {
        column.sorter = (a, b) => {
          const aVal = a[col.dataIndex];
          const bVal = b[col.dataIndex];
          if (typeof aVal === 'string') {
            return aVal.localeCompare(bVal);
          }
          return aVal - bVal;
        };
      }

      // Filterable (dropdown)
      if (col.filterable && col.filterOptions) {
        column.filters = col.filterOptions.map(opt => ({ text: opt, value: opt }));
        column.onFilter = (value, record) => record[col.dataIndex] === value;
      }

      // Searchable (inline search in column header)
      if (col.searchable) {
        column.filterDropdown = ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
          <div style={{ padding: 8 }}>
            <Input
              placeholder={`Search ${col.title}`}
              value={selectedKeys[0]}
              onChange={e => setSelectedKeys(e.target.value ? [e.target.value] : [])}
              onPressEnter={() => confirm()}
              style={{ marginBottom: 8, display: 'block' }}
            />
          </div>
        );
        column.filterIcon = filtered => (
          <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />
        );
        column.onFilter = (value, record) =>
          record[col.dataIndex]
            ?.toString()
            .toLowerCase()
            .includes(value.toLowerCase());
      }

      // Clickable column
      if (col.clickable && col.clickUrl) {
        column.render = (text, record) => (
          <a
            onClick={() => handleRowClick(record, col.clickUrl)}
            style={{ color: '#1890ff', cursor: 'pointer' }}
          >
            {text}
          </a>
        );
      }

      return column;
    });
  };

  // Handle row click for clickable columns
  const handleRowClick = async (record, urlTemplate) => {
    try {
      setLoading(true);
      
      // Interpolate URL template with record data
      let url = urlTemplate;
      Object.keys(record).forEach(key => {
        url = url.replace(`{${key}}`, record[key]);
      });

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${sessionId}`
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch: ${response.status}`);
      }

      const result = await response.json();
      
      // Expect API to return new apiData structure
      if (result.apiData && onComponentUpdate) {
        onComponentUpdate(result.apiData);
        message.success('Content loaded');
      }
    } catch (error) {
      console.error('Error loading content:', error);
      message.error('Failed to load content');
    } finally {
      setLoading(false);
    }
  };

  // Row selection config
  const rowSelection = selectionMode ? {
    type: selectionMode === 'single' ? 'radio' : 'checkbox',
    selectedRowKeys,
    onChange: (keys) => {
      setSelectedRowKeys(keys);
    }
  } : null;

  return (
    <div style={{ 
      marginBottom: '4px', 
      boxShadow: '0 2px 6px rgba(0, 0, 0, 0.1)',
      borderRadius: '4px'
    }}>
      <Collapse
        defaultActiveKey={['1']}
        expandIcon={({ isActive }) => isActive ? <DownOutlined /> : <RightOutlined />}
        items={[
          {
            key: '1',
            label: (
              <div style={{ fontWeight: 'bold', fontSize: '18px' }}>
                {title}
                {selectedRowKeys.length > 0 && (
                  <span style={{ marginLeft: '12px', fontSize: '14px', color: '#666' }}>
                    ({selectedRowKeys.length} selected)
                  </span>
                )}
              </div>
            ),
            children: (
              <div>
                <Table
                  columns={buildColumns()}
                  dataSource={filteredData}
                  rowKey={record => record.id || record.key}
                  rowSelection={rowSelection}
                  loading={loading}
                  pagination={{
                    pageSize: pageSize,
                    showSizeChanger: true,
                    showTotal: (total) => `Total ${total} items`
                  }}
                  bordered
                  size="small"
                />
              </div>
            )
          }
        ]}
      />
    </div>
  );
};

export default AdvancedTableDisplay;